/** Display only. Explicit offsets use one Korean calendar/time; unspecified zones stay unspecified. */
export function workspaceDateTime(value: unknown): string {
    const text = String(value ?? '').trim();
    if (!text) return '—';
    const match = text.match(/^(\d{4})[-.](\d{2})[-.](\d{2})[T\s](\d{2}):(\d{2})/);
    if (!match) return text;
    if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) return `${match[1]}.${match[2]}.${match[3]} ${match[4]}:${match[5]} (시간대 미지정)`;
    const date = new Date(text);
    if (!Number.isFinite(date.getTime())) return text;
    const parts = new Intl.DateTimeFormat('en-GB', {timeZone:'Asia/Seoul', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', hourCycle:'h23'}).formatToParts(date);
    const part = (type: string) => parts.find(p => p.type === type)?.value || '';
    return `${part('year')}.${part('month')}.${part('day')} ${part('hour')}:${part('minute')} (한국 시간)`;
}

/** Opt-in UI dialogs only. Close through the owner's existing cancel/dirty contract. */
export class WorkspaceDialogFocus {
    private observer: MutationObserver;
    private current: HTMLElement | null = null;
    private opener: HTMLElement | null = null;
    private inerted = new Map<HTMLElement, boolean>();
    private scrollRoot: HTMLElement | null = null;
    private overflow = '';
    constructor(private root: HTMLElement, private blocked: () => boolean) {
        this.observer = new MutationObserver(() => this.refresh());
        this.observer.observe(root, {subtree:true, childList:true, attributes:true, attributeFilter:['class','aria-hidden','hidden']});
        root.addEventListener('keydown', this.keydown, true);
        this.refresh();
    }
    private visible = (node: HTMLElement) => node.isConnected && !node.closest('[hidden],[aria-hidden="true"]') && node.getClientRects().length > 0;
    private items(dialog: HTMLElement) {
        return Array.from(dialog.querySelectorAll<HTMLElement>('a[href],button:not(:disabled),input:not(:disabled):not([type="hidden"]),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])'))
            .filter(node => this.visible(node) && !node.closest('[inert]'));
    }
    private unlock() {
        for (const [node, previous] of this.inerted) node.inert = previous;
        this.inerted.clear();
        if (this.scrollRoot) this.scrollRoot.style.overflow = this.overflow;
        this.scrollRoot = null;
    }
    private lock(dialog: HTMLElement) {
        let child: HTMLElement = dialog;
        while (child.parentElement && child !== this.root) {
            for (const sibling of Array.from(child.parentElement.children)) {
                if (sibling instanceof HTMLElement && sibling !== child && !sibling.matches('wiz-portal-season-modal,.qa-approval-backdrop') && !this.inerted.has(sibling)) {
                    this.inerted.set(sibling, sibling.inert); sibling.inert = true;
                }
            }
            child = child.parentElement;
        }
        this.scrollRoot = dialog.closest<HTMLElement>('.reviewops-route-stage > router-outlet + *');
        if (this.scrollRoot) { this.overflow = this.scrollRoot.style.overflow; this.scrollRoot.style.overflow = 'hidden'; }
    }
    private refresh() {
        const dialogs = Array.from(this.root.querySelectorAll<HTMLElement>('[data-ro-dialog]')).filter(this.visible);
        const next = dialogs[dialogs.length - 1] || null;
        if (next !== this.current) {
            const previous = this.current;
            const restore = previous && (previous.contains(document.activeElement) || document.activeElement === document.body);
            this.unlock(); this.current = next;
            if (next) {
                this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                next.tabIndex = -1;
                if (!this.blocked()) {
                    this.lock(next);
                    (next.querySelector<HTMLElement>('[autofocus]') || this.items(next)[0] || next).focus({preventScroll:true});
                }
            } else {
                if (restore && this.opener?.isConnected && !this.opener.closest('[inert]')) this.opener.focus({preventScroll:true});
                this.opener = null;
            }
        } else if (next) {
            if (this.blocked()) this.unlock();
            else if (!this.inerted.size) this.lock(next);
        }
    }
    private keydown = (event: KeyboardEvent) => {
        const dialog = this.current;
        if (!dialog || this.blocked() || event.defaultPrevented || !this.visible(dialog)) return;
        if (event.key === 'Escape') {
            // Inner choice menus keep their own Escape behavior.
            if ((event.target as HTMLElement)?.closest('[role="listbox"]') || dialog.querySelector('[aria-expanded="true"][aria-haspopup]')) return;
            const close = dialog.querySelector<HTMLButtonElement>('[data-ro-dialog-close]:not(:disabled)');
            if (close) { event.preventDefault(); event.stopImmediatePropagation(); close.click(); }
        } else if (event.key === 'Tab') {
            const items = this.items(dialog), first = items[0], last = items[items.length - 1];
            if (!first) { event.preventDefault(); dialog.focus(); }
            else if (!dialog.contains(document.activeElement) || event.shiftKey && document.activeElement === first) {
                event.preventDefault(); (event.shiftKey ? last : first).focus();
            } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
    };
    destroy() { this.observer.disconnect(); this.root.removeEventListener('keydown', this.keydown, true); this.unlock(); this.current = null; this.opener = null; }
}
